import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  formatInteger,
  importReportEntityLabels,
  type ImportConflict,
  type ImportError,
  type ImportHint,
  type ImportReportProperty,
} from '@brimo/shared';
import { clsx } from '../../../../ds/clsx';
import {
  Banner,
  Pill,
  Progress,
  Skeleton,
  Table,
  TextLink,
  type TableColumn,
} from '../../../../ds/components';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import { useImportRun } from './api';
import {
  bytesLabel,
  countsRows,
  dateTimeLabel,
  durationLabel,
  isRunActive,
  outcomeColumns,
  progressLine,
  runStatusTones,
  startedByLabel,
  type CountsRow,
} from './report';
import styles from './IntegrationsPage.module.css';

/** Run detail (D-190): counts per entity, conflicts, errors, hints, objects, files; polls while running. */
export function IntegrationRunPage() {
  const { runId = '' } = useParams();
  const run = useImportRun(runId);
  const [now, setNow] = useState(() => Date.now());
  const active = run.data ? isRunActive(run.data.status) : false;

  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [active]);

  const data = run.data;
  const title = data ? `${data.modeLabel} vom ${dateTimeLabel(data.createdAt)}` : 'Lauf';
  const back = (
    <TextLink to="/settings/organization/integrations" size="small">
      Zurück zu Integrationen
    </TextLink>
  );

  return (
    <OrganizationPage
      title={title}
      description={back}
      actions={data ? <Pill variant={runStatusTones[data.status]}>{data.statusLabel}</Pill> : null}
    >
      {run.isError ? <QueryError error={run.error} /> : null}
      {run.isPending ? <Skeleton variant="block" height={260} /> : null}
      {data ? (
        <div className={styles.stack}>
          <section className={styles.card} aria-label="Überblick">
            <dl className={styles.facts}>
              <div className={styles.fact}>
                <dt>Gestartet</dt>
                <dd>
                  {dateTimeLabel(data.startedAt ?? data.createdAt)}
                  <span className={styles.factMeta}>von {startedByLabel(data)}</span>
                </dd>
              </div>
              <div className={styles.fact}>
                <dt>Dauer</dt>
                <dd>{durationLabel(data, now)}</dd>
              </div>
              <div className={styles.fact}>
                <dt>Dateien</dt>
                <dd>
                  {data.report
                    ? `${formatInteger(data.report.files.downloaded)} ${data.mode === 'dry_run' ? 'zu laden' : 'geladen'}`
                    : '—'}
                  <span className={styles.factMeta}>
                    {data.report
                      ? `${bytesLabel(data.report.files.bytes)} · ${formatInteger(data.report.files.reused)} bereits vorhanden`
                      : ''}
                  </span>
                </dd>
              </div>
            </dl>
            {active ? (
              <div className={styles.progress}>
                <Progress
                  label="Fortschritt des Laufs"
                  value={
                    data.progress && data.progress.total > 0
                      ? (data.progress.processed / data.progress.total) * 100
                      : 0
                  }
                />
                <span className={styles.actionsHint}>
                  {progressLine(data.progress) ?? 'Wartet auf den Start …'} · aktualisiert sich alle 3
                  Sekunden
                </span>
              </div>
            ) : null}
            {data.mode === 'dry_run' && !active ? (
              <p className={styles.note}>
                Probelauf: Es wurde nichts gespeichert. Die Zahlen zeigen, was ein Import ändern würde.
              </p>
            ) : null}
            {data.error ? <Banner tone="danger">{data.error}</Banner> : null}
          </section>

          {data.report ? (
            <>
              <CountsCard rows={countsRows(data.report.counts)} />
              <ConflictsCard items={data.report.conflicts} />
              <ErrorsCard items={data.report.errors} />
              <HintsCard items={data.report.hints} />
              <PropertiesCard items={data.report.properties} />
            </>
          ) : null}
        </div>
      ) : null}
    </OrganizationPage>
  );
}

function CountsCard({ rows }: { rows: CountsRow[] }) {
  const columns: TableColumn<CountsRow>[] = [
    {
      id: 'entity',
      header: 'Bereich',
      track: 'minmax(0, 1.6fr)',
      cell: (row) => <span className={clsx(row.total && styles.cellStrong)}>{row.label}</span>,
    },
    ...outcomeColumns.map(({ outcome, label }): TableColumn<CountsRow> => ({
      id: outcome,
      header: label,
      numeric: true,
      cell: (row) => {
        const value = row.counts[outcome];
        const warn = value > 0 && (outcome === 'conflict' || outcome === 'failed');
        return (
          <span
            className={clsx(
              value === 0 && styles.zero,
              warn && styles.warnValue,
              row.total && styles.cellStrong,
            )}
          >
            {formatInteger(value)}
          </span>
        );
      },
    })),
  ];
  return (
    <section className={styles.card} aria-labelledby="run-counts">
      <span id="run-counts" className={styles.eyebrow}>
        Ergebnis je Bereich
      </span>
      <Table
        label="Ergebnis je Bereich"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        density="compact"
      />
    </section>
  );
}

function ConflictsCard({ items }: { items: ImportConflict[] }) {
  return (
    <section className={styles.card} aria-labelledby="run-conflicts">
      <span id="run-conflicts" className={styles.eyebrow}>
        Konflikte · {formatInteger(items.length)}
      </span>
      {items.length === 0 ? (
        <p className={styles.emptyLine}>Keine Konflikte.</p>
      ) : (
        <ul className={styles.list}>
          {items.map((item, index) => (
            <li key={`${item.entity}-${item.externalId}-${index}`} className={styles.listItem}>
              <span className={styles.cellStrong}>{item.reasonLabel}</span>
              <span className={styles.cellMeta}>
                {importReportEntityLabels[item.entity]} · TeamProQ-ID {item.externalId}
                {item.field ? ` · Feld ${item.field}` : ''}
              </span>
              {item.damoklesValue || item.externalValue ? (
                <span className={styles.compare}>
                  <span>
                    <span className={styles.compareLabel}>DAMOKLES</span> {item.damoklesValue ?? '—'}
                  </span>
                  <span>
                    <span className={styles.compareLabel}>TeamProQ</span> {item.externalValue ?? '—'}
                  </span>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ErrorsCard({ items }: { items: ImportError[] }) {
  return (
    <section className={styles.card} aria-labelledby="run-errors">
      <span id="run-errors" className={styles.eyebrow}>
        Fehler · {formatInteger(items.length)}
      </span>
      {items.length === 0 ? (
        <p className={styles.emptyLine}>Keine Fehler.</p>
      ) : (
        <ul className={styles.list}>
          {items.map((item, index) => (
            <li key={`${item.entity}-${item.externalId}-${index}`} className={styles.listItem}>
              <span className={clsx(styles.cellStrong, styles.warnValue)}>{item.message}</span>
              <span className={styles.cellMeta}>
                {importReportEntityLabels[item.entity]} · TeamProQ-ID {item.externalId}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HintsCard({ items }: { items: ImportHint[] }) {
  if (items.length === 0) return null;
  return (
    <section className={styles.card} aria-labelledby="run-hints">
      <span id="run-hints" className={styles.eyebrow}>
        Hinweise · {formatInteger(items.length)}
      </span>
      <ul className={styles.list}>
        {items.map((item, index) => (
          <li key={`${item.entity}-${item.externalId}-${index}`} className={styles.listItem}>
            <span className={styles.cellText}>{item.reasonLabel}</span>
            <span className={styles.cellMeta}>
              {importReportEntityLabels[item.entity]} · TeamProQ-ID {item.externalId}
              {item.detail ? ` · ${item.detail}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PropertiesCard({ items }: { items: ImportReportProperty[] }) {
  const columns: TableColumn<ImportReportProperty>[] = [
    {
      id: 'name',
      header: 'Objekt',
      track: 'minmax(0, 2fr)',
      cell: (row) => <span className={styles.cellStrong}>{row.name}</span>,
    },
    { id: 'category', header: 'Ordner', cell: (row) => row.category ?? '—' },
    {
      id: 'units',
      header: 'Einheiten',
      numeric: true,
      track: '90px',
      cell: (row) => formatInteger(row.unitCount),
    },
    {
      id: 'included',
      header: 'Auswahl',
      track: '130px',
      cell: (row) => (row.included ? 'importiert' : <span className={styles.cellMeta}>ausgeschlossen</span>),
    },
    {
      id: 'damokles',
      header: 'In DAMOKLES',
      track: 'minmax(0, 1.2fr)',
      cell: (row) =>
        row.damoklesPropertyId ? (
          <Link className={styles.link} to={`/properties/${row.damoklesPropertyId}`}>
            Objekt öffnen
          </Link>
        ) : (
          <span className={styles.cellMeta}>—</span>
        ),
    },
  ];
  return (
    <section className={styles.card} aria-labelledby="run-properties">
      <span id="run-properties" className={styles.eyebrow}>
        Objekte · {formatInteger(items.length)}
      </span>
      <Table
        label="Objekte des Laufs"
        columns={columns}
        rows={items}
        rowKey={(row) => row.externalId}
        density="compact"
        emptyText="Keine Objekte."
      />
    </section>
  );
}
